import { AnimatePresence, motion } from 'framer-motion'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import IcebreakStartMessage from '../components/IcebreakStartMessage'
import TestStartMessage from '../components/TestStartMessage'
import OpacityAnimation from '@/components/animations/OpacityAnimation'
import BotMessage from '@/components/chat/BotMessage.tsx'
import RecordingAudioWithTimer from '@/components/chat/RecordingAudioWithTimer'
import TopicSelector from '@/components/chat/topic-selector'
import UserMessage from '@/components/chat/UserMessage'
import { Column, ProgressBar, Show } from '@/components/core'
import { Match, Switch } from '@/components/switch'
import { useAuth } from '@/contexts/AuthContext.tsx'
import { useAutoPlay } from '@/contexts/AutoPlayContext'
import { MessageType, useTestChat } from '@/contexts/TestChatContext.tsx'
import DidntUnderstandButton from '@/features/Testing/components/DidntUnderstandButton.tsx'
import VideoRecordingBox from '@/features/Testing/components/VideoRecordingBox.tsx'
import { ROUTES } from '@/types/routes.ts'

const TestChat: FC = () => {
  const navigate = useNavigate()
  const { playingUrl } = useAutoPlay()
  const { configurations } = useAuth()
  const {
    isUserTurn,
    isTestFinished,
    isStreamConnecting,
    messages,
    availableTopics,
    handleStart,
    handleSendUserReply,
    handleSelectTopics,
    handleSimplifyQuestion,
    stopStream
  } = useTestChat()

  const scrollDivRef = useRef<HTMLDivElement>(null)

  const [
    showRecordingTimeRecommendationTooltip,
    setShowRecordingTimeRecommendationTooltip
  ] = useState(false)

  useEffect(() => {
    handleStart()

    const scrollDiv = setInterval(() => {
      if (scrollDivRef.current) {
        scrollDivRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'end'
        })
      }
    }, 500)
    return () => {
      clearInterval(scrollDiv)
    }
  }, [])

  useEffect(() => {
    if (!isTestFinished || playingUrl) return

    const finishTest = async () => {
      if (configurations.shouldRecordVideo) {
        await stopStream()
      }
      navigate(
        configurations.showTestResultToStudent
          ? ROUTES.PROCESSING
          : ROUTES.COMPLETED
      )
    }

    finishTest()
  }, [
    navigate,
    isTestFinished,
    playingUrl,
    stopStream,
    configurations.showTestResultToStudent,
    configurations.shouldRecordVideo
  ])

  const shouldUserReply = !playingUrl && isUserTurn && !isTestFinished

  const progressBarWidth = useMemo(() => {
    const userMessagesCount = messages.filter(
      (m) => m.type === MessageType.USER && !m.pending
    ).length

    const percentage = userMessagesCount / 12
    return percentage > 0.92 ? 0.91666 : percentage
  }, [messages])

  const handleRecordEnd = useCallback(
    (blob: Blob, seconds: number) => {
      if (blob) {
        const isIcebreak = !messages.some(
          (message) => message.type === MessageType.ICEBREAK_DIVISOR
        )

        if (!isIcebreak) {
          const recordsLessThen20s = messages.some((message) => {
            return (
              message.type === MessageType.USER &&
              (message.data?.audioSeconds || 0) < 20 &&
              message.data?.content !== "Sorry, I didn't understand." &&
              message.data?.timeline === 'chat'
            )
          })
          if (seconds < 20 && !recordsLessThen20s) {
            setShowRecordingTimeRecommendationTooltip(true)
            return
          }
        }

        handleSendUserReply(blob, seconds)
        setShowRecordingTimeRecommendationTooltip(false)
      }
    },
    [messages, handleSendUserReply]
  )

  const handleTooltipConfirm = useCallback(
    (blob: Blob, seconds: number) => {
      handleSendUserReply(blob, seconds)
      setShowRecordingTimeRecommendationTooltip(false)
    },
    [handleSendUserReply]
  )

  return (
    <Column className="h-full w-full relative">
      {configurations.shouldRecordVideo && <VideoRecordingBox />}
      {isStreamConnecting && (
        <OpacityAnimation className="absolute inset-0 z-40 flex items-center justify-center">
          <svg
            viewBox="0 0 38 38"
            xmlns="http://www.w3.org/2000/svg"
            stroke="currentColor"
            className="text-white h-7 w-7"
          >
            <g fill="none" fillRule="evenodd">
              <g transform="translate(1 1)" strokeWidth="2">
                <circle strokeOpacity=".5" cx="18" cy="18" r="18" />
                <path d="M36 18c0-9.94-8.06-18-18-18">
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    from="0 18 18"
                    to="360 18 18"
                    dur="1s"
                    repeatCount="indefinite"
                  />
                </path>
              </g>
            </g>
          </svg>
        </OpacityAnimation>
      )}
      <Column className="relative h-full max-h-[calc(100vh-82px)] w-full overflow-hidden pt-2 lg:rounded-tr-2xl">
        <ProgressBar
          className={{
            wrapper:
              'opacity-1 absolute left-0 top-0 h-[4px] w-full rounded-none bg-[#9675c7]',
            bar: 'absolute top-0 h-[4px] bg-[#09d4e0]'
          }}
          progress={progressBarWidth}
        />
        <Column className="h-full w-full justify-between self-center max-lg:px-4 md:max-w-[748px] md:space-y-20">
          <Column className="w-full overflow-hidden max-md:max-h-[calc(80vh-120px)]">
            {messages.map((message, index) => {
              const overlayed = index < messages.length - 2

              return (
                <motion.div
                  key={`container-${message.key}`}
                  animate={{ opacity: overlayed ? 0.5 : 1 }}
                  className="flex w-full flex-col opacity-100"
                >
                  <Show when={index > messages.length - 6}>
                    <Switch>
                      <Match when={message.type === MessageType.ICEBREAK_START}>
                        <IcebreakStartMessage />
                      </Match>
                      <Match when={message.type === MessageType.ASSISTANT}>
                        <OpacityAnimation className="w-full py-4 max-lg:max-w-[80%]">
                          <BotMessage
                            key={message.key}
                            audioUrl={
                              message?.data?.audioUrl
                                ? `${
                                    import.meta.env.VITE_RECORD_BASE_URL
                                  }/${message?.data?.audioUrl}`
                                : undefined
                            }
                            variant="primary"
                            pendingTime={1500}
                            autoPlay
                            recordingFeedback={message?.pending}
                            pending={message?.pending}
                          />
                          <Show
                            when={
                              shouldUserReply &&
                              index == messages.length - 1 &&
                              !message.pending
                            }
                          >
                            <OpacityAnimation className="mt-3">
                              <DidntUnderstandButton
                                onClick={handleSimplifyQuestion}
                              />
                            </OpacityAnimation>
                          </Show>
                        </OpacityAnimation>
                      </Match>
                      <Match when={message.type === MessageType.USER}>
                        <OpacityAnimation className="flex-column flex w-full justify-end py-8 max-lg:pl-[20%]">
                          <UserMessage
                            key={message.key}
                            audioUrl={message?.data?.recordUrl}
                            audioBlob={message?.data?.audioBlob}
                            pending={message.pending}
                            recordingFeedback={message.pending}
                          />
                        </OpacityAnimation>
                      </Match>
                      <Match
                        when={message.type === MessageType.TOPIC_SELECTION}
                      >
                        <OpacityAnimation className="w-full pt-4">
                          <TopicSelector
                            topics={availableTopics}
                            maxTopicsSelection={2}
                            onConfirm={handleSelectTopics}
                          />
                        </OpacityAnimation>
                      </Match>
                      <Match
                        when={message.type === MessageType.ICEBREAK_DIVISOR}
                      >
                        <TestStartMessage />
                      </Match>
                    </Switch>
                  </Show>
                </motion.div>
              )
            })}
            <div ref={scrollDivRef} />
          </Column>
          <Column className="h-[300px] max-h-[20vh] w-full items-center justify-end pb-4 md:max-h-[30vh]">
            <AnimatePresence>
              {shouldUserReply && (
                <motion.div
                  key="recording-user"
                  initial={{ opacity: 0 }}
                  animate={{
                    opacity: 1
                  }}
                  exit={{ opacity: 0 }}
                  transition={{ ease: 'easeOut', duration: 0.07 }}
                  className="flex w-full max-w-[488px] flex-col"
                >
                  <RecordingAudioWithTimer
                    variant="secondary"
                    onRecordingEnd={handleRecordEnd}
                    onTooltipConfirm={handleTooltipConfirm}
                    timerInSeconds={3}
                    waveHeight={48}
                    waveScalling={3}
                    showRecordingTimeRecommendationTooltip={
                      showRecordingTimeRecommendationTooltip
                    }
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </Column>
        </Column>
      </Column>
    </Column>
  )
}

export default TestChat
